/**
 * TGExportMenu
 * Export to CSV or XLSX with current view or full dataset
 */

import React, { useState } from 'react';
import { Button, Dropdown, Modal, Radio, Checkbox, Space, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx-js-style';

/**
 * Export data to CSV
 */
const exportToCSV = (data, columns, filename) => {
  if (!data || data.length === 0) {
    message.warning('No data to export');
    return;
  }

  // Get headers
  const headers = columns.map((col) => col.title);

  // Build rows
  const rows = data.map((row) =>
    columns.map((col) => {
      let value = row[col.dataIndex];

      // Use exportTransform if provided
      if (col.exportTransform) {
        value = col.exportTransform(value, row);
      }

      // Handle nulls
      if (value === null || value === undefined) return '';

      // Handle objects/arrays
      if (typeof value === 'object') return JSON.stringify(value);

      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    })
  );

  const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
};

/**
 * Export data to XLSX with styled headers
 */
const exportToXLSX = (data, columns, filename) => {
  if (!data || data.length === 0) {
    message.warning('No data to export');
    return;
  }

  // Build worksheet data
  const headers = columns.map((col) => col.title);
  const rows = data.map((row) =>
    columns.map((col) => {
      let value = row[col.dataIndex];

      // Use exportTransform if provided
      if (col.exportTransform) {
        value = col.exportTransform(value, row);
      }

      return value ?? '';
    })
  );

  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Style header row - bold text with background color
  // Using xlsx-js-style for full styling support
  columns.forEach((col, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    if (ws[cellAddress]) {
      ws[cellAddress].s = {
        font: {
          bold: true,
          sz: 11,
          color: { rgb: '000000' },
        },
        fill: {
          fgColor: { rgb: 'E4E1E8' }, // rgb(228, 225, 232)
        },
        alignment: {
          vertical: 'center',
          horizontal: 'left',
        },
        border: {
          bottom: { style: 'thin', color: { rgb: '000000' } },
        },
      };
    }
  });

  // Set column widths
  ws['!cols'] = columns.map((col) => ({ wch: col.width ? col.width / 8 : 15 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * @param {Object} props
 * @param {Array} props.data - All loaded data
 * @param {Array} props.currentPageData - Current page data only
 * @param {Array} props.columns - Visible columns
 * @param {Array} props.allColumns - All columns
 * @param {Function} [props.fetchAll] - Async function to fetch all data
 * @param {boolean} [props.isPaginatedClient] - True if using client-side pagination
 * @param {string} [props.filename] - Export filename
 */
function TGExportMenu({ data, currentPageData, columns, allColumns, fetchAll, transformForExport, filename = 'export', isPaginatedClient = false }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportScope, setExportScope] = useState('current');
  const [includeAllColumns, setIncludeAllColumns] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);

    try {
      // Determine data to export
      let exportData;
      if (exportScope === 'current') {
        exportData = currentPageData || data;
      } else if (exportScope === 'all') {
        if (fetchAll) {
          exportData = await fetchAll();
        } else {
          exportData = data; // All loaded data
        }
      } else {
        exportData = data;
      }

      // Transform data if transformForExport is provided
      if (transformForExport) {
        const transformed = transformForExport(exportData);
        // When using transformForExport, create columns from the first row
        if (transformed && transformed.length > 0) {
          const transformedColumns = Object.keys(transformed[0]).map((key) => ({
            title: key,
            dataIndex: key,
            key: key,
          }));

          if (exportFormat === 'csv') {
            exportToCSV(transformed, transformedColumns, filename);
          } else {
            exportToXLSX(transformed, transformedColumns, filename);
          }

          message.success(`Exported ${transformed.length} rows with all event details`);
          setModalVisible(false);
          setExporting(false);
          return;
        }
      }

      // Default export with columns
      const exportColumns = includeAllColumns ? allColumns : columns;

      // Export based on format
      if (exportFormat === 'csv') {
        exportToCSV(exportData, exportColumns, filename);
      } else {
        exportToXLSX(exportData, exportColumns, filename);
      }

      message.success(`Exported ${exportData.length} rows`);
      setModalVisible(false);
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const menuItems = [
    {
      key: 'csv',
      label: 'Export to CSV',
      onClick: () => {
        setExportFormat('csv');
        setModalVisible(true);
      },
    },
    {
      key: 'xlsx',
      label: 'Export to Excel (XLSX)',
      onClick: () => {
        setExportFormat('xlsx');
        setModalVisible(true);
      },
    },
  ];

  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
        <Button icon={<DownloadOutlined />}>Export</Button>
      </Dropdown>

      <Modal
        title={`Export to ${exportFormat.toUpperCase()}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleExport}
        confirmLoading={exporting}
        okText="Export"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Export Scope</div>
            <Radio.Group value={exportScope} onChange={(e) => setExportScope(e.target.value)}>
              <Space direction="vertical">
                <Radio value="current">
                  Current page ({(currentPageData || data).length} rows)
                </Radio>
                <Radio value="all">
                  All data ({data.length} rows{!isPaginatedClient && fetchAll ? ', will fetch more' : ''})
                </Radio>
              </Space>
            </Radio.Group>
          </div>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={includeAllColumns}
              onChange={(e) => setIncludeAllColumns(e.target.checked)}
            >
              Include hidden columns ({allColumns.length - columns.length} hidden)
            </Checkbox>
          </div>
        </Space>
      </Modal>
    </>
  );
}

export default TGExportMenu;
